/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Card, CardContent, CircularProgress, Link, Stack, Tooltip, Typography } from "@mui/material";

import { useNavigate } from "react-router-dom";

import { useWindowDimensions } from "../../utils/Hooks";

import { ExpandedAssignment, ExpandedWorkOrder } from "../../utils/Interfaces";
import DataTable from "../../components/table/DataTable";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { getGoogleMapsURL } from "../../utils/Functions";
import DirectionsIcon from "@mui/icons-material/Directions";
import InfoModal from "../../components/modal/InfoModal";
import { useServiceLocationDetails } from "./workSiteHooks";

const WorkSiteDetails = () => {
    const { height } = useWindowDimensions();
    const { siteId } = useParams();

    const [activeTitle, setActiveTitle] = useState<string>("");
    const [activeDescription, setActiveDescription] = useState<string>("");
    const [descriptionModalOpen, setDescriptionModalOpen] = useState<boolean>(false);

    const navigate = useNavigate();

    const { data, status } = useServiceLocationDetails(parseInt(siteId ?? "1"));

    function getTripCount(workOrder: ExpandedWorkOrder): number {
        let technicianEntryIds: number[] = [];
        workOrder.assignments.map((assignment: ExpandedAssignment) => {
            if (!technicianEntryIds.includes(assignment.technician_entry.id)) {
                technicianEntryIds.push(assignment.technician_entry.id);
            }
            return null;
        });
        return technicianEntryIds.length;
    }

    function getAssignees(workOrder: ExpandedWorkOrder): string {
        let assignees: string[] = [];

        workOrder.assignments.map((assignment: ExpandedAssignment) => {
            let assigneeName = `${assignment.technician.first_name} ${assignment.technician.last_name}`;
            if (!assignees.includes(assigneeName)) {
                assignees.push(assigneeName);
            }
            return assigneeName;
        });

        return assignees.join(", ");
    }

    const columns = [
        { field: "id", headerName: "id", filterable: false, sortable: true },
        {
            field: "ticket_no",
            width: 250,
            headerName: "Ticket #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.ticket_no;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/work-orders/${params.row.id}`)}>
                        <Typography
                            variant="overline"
                            fontSize={14}
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", width: "200px" }}
                        >
                            {params.row.ticket_no}
                        </Typography>
                    </Link>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            type: "singleSelect",
            sortable: true,
            valueOptions: ["UN", "IP", "SB", "CN", "AR"],
            filterable: true,
        },
        {
            field: "trip_number",
            width: 70,
            headerName: "Trip #",
            sortable: true,
            filterable: false,
            valueGetter: (params: GridRenderCellParams) => {
                return getTripCount(params.row);
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography>{getTripCount(params.row)}</Typography>;
            },
        },
        {
            field: "date_completed",
            width: 200,
            headerName: "Date Completed",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.date_completed;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography>{params.row.date_completed ? params.row.date_completed : "--"}</Typography>;
            },
        },
        {
            field: "assignee",
            headerName: "Assignees",
            width: 180,
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return getAssignees(params.row);
            },
            renderCell: (params: GridRenderCellParams) => {
                let assignees: string = getAssignees(params.row);
                let leadAssignee: string = assignees;
                let assigneesSplit: string[] = assignees.split(",");

                leadAssignee = assigneesSplit[0];

                if (assigneesSplit.length > 1) {
                    return (
                        <Tooltip title={assignees}>
                            <Link
                                onClick={() => {
                                    setActiveTitle(`Assignees (${params.row.ticket_no})`);
                                    setActiveDescription(assignees);
                                    setDescriptionModalOpen(true);
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    noWrap
                                    sx={{ textOverflow: "ellipsis", width: 145, cursor: "pointer" }}
                                >
                                    {`${leadAssignee}, +${assigneesSplit.length - 1}`}
                                </Typography>
                            </Link>
                        </Tooltip>
                    );
                }

                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 175 }}>
                        {leadAssignee}
                    </Typography>
                );
            },
        },
        {
            width: 250,
            field: "device_to_be_fixed",
            headerName: "Device to be Fixed",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.device_to_be_fixed;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.device_to_be_fixed}>
                        <Typography>{params.row.device_to_be_fixed}</Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: "problem_description",
            headerName: "Problem Description",
            width: 200,
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.problem_description;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.problem_description}>
                        <Link
                            onClick={() => {
                                setActiveTitle(`Problem Description (${params.row.ticket_no})`);
                                setActiveDescription(params.row.problem_description);
                                setDescriptionModalOpen(true);
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{ textOverflow: "ellipsis", width: 195, cursor: "pointer" }}
                            >
                                {params.row.problem_description}
                            </Typography>
                        </Link>
                    </Tooltip>
                );
            },
        },
    ];

    // useEffect(() => {
    //     updateWorkSite(parseInt(siteId as string), dispatch, setSite, setWorkOrders, setLoading);
    // }, [dispatch, siteId]);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    { text: "Service Locations", route: "/work-sites" },
                    data
                        ? { text: `${data.site.company.name} #${data.site.store_no}`, route: `/work-sites/${data.site.id}` }
                        : null,
                ]}
            />

            <InfoModal
                title={activeTitle}
                description={activeDescription}
                open={descriptionModalOpen}
                handleClose={() => setDescriptionModalOpen(false)}
            />

            {status === "pending" && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 20 }}>
                    <CircularProgress size={40} />
                </Stack>
            )}

            {status === "success" && (
                <Box>
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="h6">
                                    <Link onClick={() => navigate(`/companies/${data?.site.id}`)} sx={{ cursor: "pointer" }}>
                                        {data?.site.company.name}
                                    </Link>
                                    &nbsp;#{data?.site.store_no}
                                </Typography>
                                <Tooltip title="Get Directions using Google Maps">
                                    <Link href={getGoogleMapsURL(data.site)} target="_blank">
                                        <DirectionsIcon color="info" />
                                    </Link>
                                </Tooltip>
                            </Stack>
                            <Typography variant="h6">
                                {data?.site.street_address === null
                                    ? ""
                                    : `${data?.site.street_address}, ${data?.site.city}, ${data?.site.state} ${data?.site.zip_code ?? ""}`}
                            </Typography>
                        </CardContent>
                    </Card>
                    <Box style={{ paddingTop: "10px" }}>
                        <DataTable
                            tableName="WORK_SITES_DETAILS"
                            height={height - 300}
                            columns={columns}
                            data={data.work_orders}
                            allowRowSelected={false}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WorkSiteDetails;
